import { useState, useEffect } from "react";
import { EducatorUserService, AlpService, ServiceStatusEnum } from "@imaginelearning/mps-api-service";
import useUser from "hooks/useuser";
import {
  StudentId,
  TTSSTProps,
  UpdatePlacementProps,
  StudentMypathAssessment
} from "../Homepage/StudentHomePage.props";
import buildPlacementData from "../Homepage/buildPlacementData";

export default function useStudentLoginDetails() {
  const [isLoading, setIsLoading] = useState(true);
  const [showIMPathway, setShowIMPathway] = useState(false);
  const [chronologicalGrade, setChronologicalGrade] = useState("");
  const [firstName, setFirstName] = useState<null | string>("");
  const [lastName, setLastName] = useState<null | string>("");
  const [schoolYear, setSchoolYear] = useState<null | string>("");
  const [studentMypathAssessment, setStudentMypathAssessment] = useState<StudentMypathAssessment>({
    math: "Off",
    reading: "Off"
  });
  const [TTSST, setTTSST] = useState<TTSSTProps>({
    tts: { math: "off", reading: "off" },
    st: { math: "off", reading: "off" }
  });
  const [allPlacementData, setAllPlacementData] = useState<UpdatePlacementProps[] | null>(null);
  const [studentId, setStudentId] = useState<StudentId>({ id: "", role: "" });
  const [gotStudentAssignments, setGotStudentAssignments] = useState(false);
  const [showMyAssignmentBadge, setShowMyAssignmentBadge] = useState(false);
  const { user, contentTypeForAlps } = useUser();

  const getStudentPlacements = async (assessmentProvider: StudentMypathAssessment) => {
    if (!user) return;
    const { status, data } = await EducatorUserService.getStudentPlacements(user.id);
    if (status === ServiceStatusEnum.ERROR) {
      // Sometimes the API returns an a 400 meaning there are no placements for the student
      setAllPlacementData(buildPlacementData(user, assessmentProvider, []));
    } else {
      setAllPlacementData(buildPlacementData(user, assessmentProvider, data));
    }
  };

  const getStudentAssignments = async (studentGuid: string) => {
    try {
      const { status, response } = await AlpService.getAlps(
        studentGuid,
        Number(user.schoolYear),
        contentTypeForAlps?.type
      );
      if (status === ServiceStatusEnum.LOADED && Array.isArray(response)) {
        setShowMyAssignmentBadge(response.filter((alp) => !alp.isCompleted).length > 0);
      }

      setGotStudentAssignments(true);
    } catch (err) {
      console.error(err, "getStudentAssignments() - Exception");
      setGotStudentAssignments(true);
    }
  };

  async function fetchStudentAssignments() {
    await getStudentAssignments(user?.id.toString());
  }

  useEffect(() => {
    const initializeStudentDetails = async () => {
      if (!user || !contentTypeForAlps) {
        return;
      }

      try {
        setIsLoading(true);

        setStudentId({
          id: user.id.toString(),
          role: user.role
        });

        const assessmentProvider: StudentMypathAssessment = {
          math: user.myPathAssessment?.some((x) => x === "MyPathMath") ? "On" : "Off",
          reading: user.myPathAssessment?.some((x) => x === "MyPathReading") ? "On" : "Off"
        };

        setStudentMypathAssessment(assessmentProvider);
        await getStudentPlacements(assessmentProvider);
        setShowIMPathway(user.organization.licensedProducts.includes("ImaginePlusMath"));
        setChronologicalGrade(user.gradeLevel.toString());
        setFirstName(user.firstname);
        setLastName(user.lastname);
        setSchoolYear(user.schoolYear !== undefined ? user.schoolYear.toString() : null);
        setTTSST({
          tts: {
            math: user.textToSpeech.includes("MyPathMath") ? "on" : "off",
            reading: user.textToSpeech.includes("MyPathReading") ? "on" : "off"
          },
          st: {
            math: user.languageTranslation.includes("MyPathMath") ? "on" : "off",
            reading: user.languageTranslation.includes("MyPathReading") ? "on" : "off"
          },
          lang: {
            math: user.spanishLanguage.includes("MyPathMath") ? "on" : "off",
            reading: user.spanishLanguage.includes("MyPathReading") ? "on" : "off"
          }
        });

        await fetchStudentAssignments();
      } catch (error) {
        console.error("Error initializing student details", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeStudentDetails();
  }, [user, contentTypeForAlps]);

  return {
    isLoading,
    showIMPathway,
    chronologicalGrade,
    firstName,
    lastName,
    studentMypathAssessment,
    TTSST,
    allPlacementData,
    studentId,
    showMyAssignmentBadge,
    gotStudentAssignments,
    schoolYear
  };
}
